/**
 * researchChecks — the single source of truth for "THE INVESTIGATION".
 *
 * The research file's cover ledger, the file-index rail's per-chapter status,
 * the mobile summit tally and the researching state's assembling animation all
 * read from THIS list. One derivation, four surfaces — so the researching
 * state can morph into the finished cover without the two disagreeing.
 *
 * HONESTY CONTRACT (design brief: "if it looks like the machine, it must BE
 * the machine"):
 *   - a row is `done` only when the data it describes is actually present in
 *     `results` — never on a timer, never on an optimistic guess;
 *   - while the run is in flight, undelivered rows are `checking` (progressive
 *     tense), because the engine really is fetching all of them;
 *   - when the run ends, rows whose data never arrived stay `missing`. They do
 *     NOT tick. "5 of 7 checks done" is a true sentence and must stay sayable.
 * There is deliberately no simulated percentage anywhere in this module — the
 * meter fills on completed rows only. (The bar it replaces ran up to 18 points
 * ahead of reality, capped at 95%.)
 */

import { assessDueDiligence } from '../../utils/dueDiligenceAssessment';

const asNumber = (v) => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const nonEmptyArray = (v) => Array.isArray(v) && v.length > 0;
// A comparable sale with no address cannot be shown on the map or list, so it
// must not be counted as a comp the reader can see.
export const shownComparableSales = (dd) =>
  Array.isArray(dd?.comparable_sales) ? dd.comparable_sales.filter((sale) => typeof sale?.address === 'string' && sale.address.trim()) : [];

/** Compact money for the rail's status column: 534500 → "$534.5K". */
const shortMoney = (v) => {
  const n = asNumber(v);
  if (n == null) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 100_000 ? 1 : 0)}K`;
  return `$${Math.round(n)}`;
};

/**
 * Were all four hazard overlays actually assessed? A field is only a real boolean
 * when the check ran; `undefined` means the source failed or never fired.
 * Zero flags from an UNRUN check must not read as "clean" — that is the same
 * trap the hero verdict guards with `hazardsWereChecked`.
 */
export const hazardsWereChecked = dd => Boolean(dd) && assessDueDiligence({ propertyFeatures: dd }).checks
  .filter(check => check.category === 'hazard').every(check => ['clear', 'flagged'].includes(check.state));

export const countHazardFlags = dd => assessDueDiligence({ propertyFeatures: dd }).checks
  .filter(check => check.category === 'hazard' && check.state === 'flagged').length;

/**
 * buildInvestigationLedger(results, loading)
 *
 * @returns {{checks: Array, done: number, total: number, allDone: boolean}}
 *   checks[]: { key, num, chapterId, label, doing, src, stat, statTone, status }
 *   status ∈ 'done' | 'checking' | 'missing'
 */
export function buildInvestigationLedger(results, loading = false) {
  const dd = results?.propertyFeatures || null;

  // Each entry declares its own truth test. `srcOf`/`statOf` only run when the
  // row is done, so they can assume their data exists.
  const defs = [
    {
      key: 'valuation',
      num: '01',
      chapterId: 'c1',
      label: 'Valuation, weighted',
      doing: 'Pulling valuation sources…',
      isDone: () => asNumber(dd?.estimate_mid) != null,
      srcOf: () => (dd?.['2nd_source_estimate_mid'] != null ? '2 SOURCES' : '1 SOURCE'),
      statOf: () => shortMoney(dd?.estimate_mid),
      statTone: 'ok',
    },
    {
      key: 'features',
      num: '02',
      chapterId: 'c2',
      label: 'Property features',
      doing: 'Reading property records…',
      isDone: () => dd?.bedrooms != null || dd?.land_size_sqm != null
        || !!dd?.property_type || !!dd?.building_type || dd?.year_built != null,
      srcOf: () => 'RECORDS',
      statOf: () => (dd?.year_built ? String(dd.year_built)
        : dd?.land_size_sqm ? `${Math.round(asNumber(dd.land_size_sqm))} m²` : null),
    },
    {
      key: 'history',
      num: '03',
      chapterId: 'c3',
      label: 'Sale & listing history',
      doing: 'Tracing sale & listing history…',
      isDone: () => nonEmptyArray(dd?.sale_history),
      srcOf: () => 'RECORDS',
      statOf: () => {
        const n = dd?.sale_history?.length || 0;
        return n ? `${n} EVENT${n > 1 ? 'S' : ''}` : null;
      },
    },
    {
      key: 'risk',
      num: '04',
      chapterId: 'c4',
      label: 'Risk & due diligence',
      doing: 'Scanning flood, bushfire, heritage, flight path…',
      // State coverage alone does not verify all four property-level checks.
      isDone: () => hazardsWereChecked(dd),
      // Scoped wording on purpose: this check measures the four hazard
      // OVERLAYS, while the chapter it summarises also carries detractors
      // (housing commission, infrastructure…). A bare "CLEAR" here claimed the
      // whole chapter was clean even when a detractor inside it was flagged.
      srcOf: () => {
        const n = countHazardFlags(dd);
        return n > 0 ? `${n} OVERLAY FLAG${n > 1 ? 'S' : ''}` : '4 OVERLAYS CLEAR';
      },
      statOf: () => {
        const n = countHazardFlags(dd);
        return n > 0 ? `${n} FLAG${n > 1 ? 'S' : ''}` : 'OVERLAYS OK';
      },
      statToneOf: () => (countHazardFlags(dd) > 0 ? 'flag' : 'ok'),
    },
    {
      key: 'planning',
      num: '05',
      chapterId: 'c5',
      label: 'Planning & upside',
      doing: 'Checking zoning, DA paths, build rules…',
      isDone: () => !!dd?.zoning_code || !!dd?.subdivision_analysis,
      // Name the council (real provenance) but drop the "… Regional Council"
      // tail — the full name wrapped the ledger row onto two lines.
      srcOf: () => (dd?.local_government_area
        ? String(dd.local_government_area)
          .replace(/\s+(regional|city|shire|municipal|district)?\s*council$/i, '')
          .toUpperCase()
        : 'PLANNING RULES'),
      statOf: () => dd?.zoning_code || (dd?.subdivision_analysis?.verdict
        ? String(dd.subdivision_analysis.verdict).replace(/_/g, ' ').toUpperCase()
        : null),
    },
    {
      key: 'neighbourhood',
      num: '06',
      chapterId: 'c6',
      label: 'Neighbourhood',
      doing: 'Building the suburb profile…',
      isDone: () => !!dd?.suburb_profile_data || !!dd?.market_insights_section,
      srcOf: () => 'MARKET DATA',
      statOf: () => {
        const median = dd?.market_insights_section?.[0]?.median_price_trend?.[0]?.cost_value;
        return median ? String(median) : null;
      },
    },
    {
      key: 'comps',
      num: '07',
      chapterId: 'c7',
      label: 'Comparable sales',
      doing: 'Comparing nearby sales…',
      isDone: () => nonEmptyArray(shownComparableSales(dd)),
      srcOf: () => {
        const n = shownComparableSales(dd).length;
        return `${n} COMP${n === 1 ? '' : 'S'}`;
      },
      statOf: () => String(shownComparableSales(dd).length),
    },
  ];

  const checks = defs.map((d) => {
    let isDone = false;
    try {
      isDone = !!d.isDone();
    } catch {
      // A malformed source payload must not blank the whole ledger — treat the
      // row as not-yet-delivered and let the run's end state label it honestly.
      isDone = false;
    }
    const status = isDone ? 'done' : loading ? 'checking' : 'missing';
    return {
      key: d.key,
      num: d.num,
      chapterId: d.chapterId,
      label: d.label,
      doing: d.doing,
      status,
      src: isDone ? (d.srcOf?.() || null) : null,
      stat: isDone ? (d.statOf?.() || null) : null,
      statTone: isDone ? (d.statToneOf?.() || d.statTone || null) : null,
    };
  });

  const done = checks.filter((ch) => ch.status === 'done').length;
  return { checks, done, total: checks.length, allDone: done === checks.length };
}

/** The seven chapters, in report order — the rail, summit chips and jump-nav
 *  all read this so the numbering can never drift between them. */
export const RESEARCH_CHAPTERS = [
  { id: 'c1', num: '01', label: 'Value', title: 'Valuation & rental' },
  { id: 'c2', num: '02', label: 'Features', title: 'Property features' },
  { id: 'c3', num: '03', label: 'History', title: 'Location & history' },
  { id: 'c4', num: '04', label: 'Risk', title: 'Risk & due diligence' },
  { id: 'c5', num: '05', label: 'Upside', title: 'Planning & upside' },
  { id: 'c6', num: '06', label: 'Area', title: 'Neighbourhood' },
  { id: 'c7', num: '07', label: 'Comps', title: 'Comparable sales' },
];

/** [08] closes the file — the handoff lands where the decision happens. */
export const NEXT_MOVE_CHAPTER = { id: 'c8', num: '08', label: 'Next', title: 'Next move' };
